import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { numberWithCommas } from "../utils/numbers";
import { useTheme } from "next-themes";
import useWindowSize from "../hooks/useWindowSize";

export default function FundPerformanceChart({ style = {}, range = "1M", data = [], xMax = null, xMin = null, isModalOpen }) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const { theme } = useTheme();

  const screenSize = useWindowSize();

  useEffect(() => {
    if (chartRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current, theme);
    }
    const handleResize = () => chartInstanceRef.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstanceRef.current?.dispose();
    };
  }, [theme]);

  useEffect(() => {
    if (!chartInstanceRef.current) return;

    // helper to build and apply options (we call it initially + on resize)
    const applyOptions = () => {
      // ensure input data is sorted ascending by timestamp (seconds)
      const sorted = (Array.isArray(data) ? data.slice() : []).sort((a, b) => a[0] - b[0]);

      // convert to [ms, price, units] for ECharts
      const priceSeries = sorted.map(([ts, price, units]) => [ts * 1000, price, units]);

      // timestamps in ms (from data) — may be empty
      const timestampsMs = priceSeries.map((p) => p[0]);

      // Determine axis time bounds: prefer xMin/xMax if provided, otherwise derive from data
      const minTimeMsRaw = typeof xMin === "number" ? xMin : timestampsMs.length ? Math.min(...timestampsMs) : null;
      const maxTimeMsRaw = typeof xMax === "number" ? xMax : timestampsMs.length ? Math.max(...timestampsMs) : null;

      // If caller didn't pass xMin/xMax and data is empty, compute a reasonable default window (last 6 months)
      const now = Date.now();
      const fallbackSixMonths = now - 1000 * 60 * 60 * 24 * 30 * 6;
      const minTimeMs = minTimeMsRaw != null ? minTimeMsRaw : fallbackSixMonths;
      const maxTimeMs = maxTimeMsRaw != null ? maxTimeMsRaw : now;

      // Compute desired tick count based on container width
      const container = chartRef.current;
      const containerWidth = container ? container.clientWidth || container.getBoundingClientRect().width : 800;

      // approx pixels per label — tune to taste
      const approxLabelPx = containerWidth <= 460 ? 70 : 90;
      // desired ticks (clamped)
      const desiredTicks = Math.min(Math.max(Math.floor(containerWidth / approxLabelPx), 2), 12);

      // splitNumber is number of segments — ticks = splitNumber + 1 (approx)
      const splitNumber = Math.max(1, desiredTicks - 1);

      // rotate labels slightly on small widths
      const rotateLabels = containerWidth < 420 ? -30 : 0;

      // Determine Y-axis bounds (same as your logic)
      const prices = priceSeries.map((p) => p[1]);
      let niceMin, niceMax;
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const diff = Math.max(1e-8, maxPrice - minPrice);
        const magnitude = Math.pow(10, Math.floor(Math.log10(diff)));
        niceMin = Math.floor(minPrice / magnitude) * magnitude;
        niceMax = Math.ceil(maxPrice / magnitude) * magnitude;
        if (niceMin === niceMax) {
          niceMin = minPrice * 0.999;
          niceMax = maxPrice * 1.001;
        }
      } else {
        niceMin = 0;
        niceMax = 1;
      }

      // x label formatter
      const xLabelFormatter = (val) => {
        const d = new Date(val);
        switch (range) {
          case "1D":
            return d.toLocaleTimeString();
          case "1M":
          case "6M":
          case "YTD":
            return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
          case "1Y":
          case "3Y":
          case "5Y":
            return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
          default:
            return d.toLocaleDateString();
        }
      };

      // palette preserved from your implementation
      const palette = {
        tooltipBg: theme === "dark" ? "rgba(26, 28, 42, 0.9)" : "rgba(203, 210, 246, 0.9)",
        tooltipBorder: theme === "dark" ? "rgba(58, 63, 97, 0.9)" : "rgba(118, 133, 209, 0.9)",
        tooltipText: theme === "dark" ? "#fff" : "#414243ff",
        axisLine: theme === "dark" ? "#ccc" : "#75777aff",
        axisPointerLine: theme === "dark" ? "#aaa" : "#726e6eff",
        tooltipLabelBg: theme === "dark" ? "#414247ff" : "#414247ff",
        splitLine: theme === "dark" ? "#7a7575ff" : "#aea9a9ff",
        lineColor: theme === "dark" ? "#b580b9ff" : "rgb(41, 124, 223)",
        areaColor: theme === "dark" ? "#9a5a9eff" : "rgb(41, 124, 223)",
        priceHighlight: theme === "dark" ? "#7CFC00" : "#2c5602ff",
        unitsColor: theme === "dark" ? "#2bc5fc" : "#1e80a3ff",
        titleColor: theme === "dark" ? "#dadae2ff" : "#1d1d1eff",
      };

      const tooltipLabel = (axisValue) => {
        const d = new Date(axisValue);
        switch (range) {
          case "1D":
            return d.toLocaleTimeString();
          case "1M":
          case "6M":
          case "YTD":
            return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
          case "1Y":
          case "3Y":
          case "5Y":
            return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
          default:
            return d.toLocaleString();
        }
      };

      const options = {
        backgroundColor: "transparent",
        tooltip: {
          trigger: "axis",
          backgroundColor: palette.tooltipBg,
          borderColor: palette.tooltipBorder,
          borderWidth: 1,
          padding: [8, 12],
          textStyle: { color: palette.tooltipText, fontSize: 12 },
          axisPointer: {
            type: "cross",
            lineStyle: { color: palette.axisPointerLine, width: 1 },
            crossStyle: { color: palette.axisPointerLine },
            label: {
              show: true,
              backgroundColor: palette.tooltipLabelBg,
              padding: [3, 5],
              borderColor: "#595757ff",
              borderWidth: 1,
              textStyle: { color: "#fff", fontSize: 12 },
            },
          },
          formatter: (params) => {
            const p = params[0];
            const label = tooltipLabel(p.axisValue);
            const priceStr = p.data && p.data[1] != null ? p.data[1].toFixed(2) : "—";
            const units = p.data && p.data[2] != null ? numberWithCommas(p.data[2]) : "—";
            return `<div style="color:${palette.titleColor};font-weight:600;margin-bottom:8px;">${label}</div>Price: <span style="color:${palette.priceHighlight}">${priceStr}</span><br/>Units Issued: <span style="color:${palette.unitsColor}">${units}</span>`;
          },
        },

        xAxis: {
          type: "time",
          boundaryGap: false,
          min: new Date(minTimeMs).toISOString(),
          max: new Date(maxTimeMs).toISOString(),
          splitNumber, // force equal segmentation
          axisLine: { lineStyle: { color: palette.axisLine } },
          axisTick: { alignWithLabel: true },
          axisLabel: {
            formatter: xLabelFormatter,
            rotate: rotateLabels,
            color: palette.axisLine,
            margin: 8,
            fontSize: 11,
            interval: 0, // show every generated tick label (we control count via splitNumber)
          },
        },

        yAxis: {
          type: "value",
          scale: true,
          position: "right",
          min: niceMin,
          max: niceMax,
          axisLine: { show: false },
          splitLine: { show: true, lineStyle: { type: "dashed", color: palette.splitLine } },
        },

        grid: { top: "10px", right: "40px", bottom: "38px", left: "10px" },

        series: [
          {
            name: "Price",
            type: "line",
            data: priceSeries,
            smooth: true,
            showSymbol: false,
            lineStyle: { width: 2, color: palette.lineColor },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: palette.areaColor },
                { offset: 1, color: "rgba(0,0,0,0)" },
              ]),
            },
          },
        ],
      };

      // If there are no points in the time window, keep series empty but maintain axis
      if (priceSeries.length === 0) {
        options.series = [{ type: "line", data: [] }];
      }

      chartInstanceRef.current.setOption(options, { notMerge: true });
    }; // end applyOptions


    // apply initially
    applyOptions();

    // Reapply options on window resize to recompute desiredTicks/responsive rotation
    const onResize = () => {
      // resize chart (visual) first
      chartInstanceRef.current?.resize();
      // then recompute options (so splitNumber / rotate adjust to new width)
      applyOptions();
    };
    window.addEventListener("resize", onResize);

    // cleanup
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [data, range, theme, xMin, xMax]);

  useEffect(() => {
    if (chartRef.current) {
      chartInstanceRef.current?.resize();
    }
  }, [screenSize, isModalOpen])

  // useEffect(() => {
  //   if (!chartInstanceRef.current) return;

  //   // const sorted = data.slice().sort((a, b) => a[0] - b[0]);
  //   // const priceSeries = sorted.map(([ts, price, units]) => [new Date(ts * 1000).toISOString(), price, units]);
  //   // const prices = sorted.map(([, price]) => price);
  //   // const times = sorted.map(([ts]) => ts * 1000);
  //   // const minPrice = Math.min(...prices);
  //   // const maxPrice = Math.max(...prices);
  //   // const diff = maxPrice - minPrice;
  //   // const magnitude = Math.pow(10, Math.floor(Math.log10(diff)));
  //   // const niceMin = Math.floor(minPrice / magnitude) * magnitude;
  //   // const niceMax = Math.ceil(maxPrice / magnitude) * magnitude;
  //   // const minTime = new Date(Math.min(...times)).toISOString();
  //   // const maxTime = new Date(Math.max(...times)).toISOString();

  //   // ensure input data is sorted ascending by timestamp (seconds)
  //   const sorted = (Array.isArray(data) ? data.slice() : []).sort((a, b) => a[0] - b[0]);

  //   // convert to [ms, price, units] for ECharts
  //   const priceSeries = sorted.map(([ts, price, units]) => [ts * 1000, price, units]);

  //   // timestamps in ms (from data) — may be empty
  //   const timestampsMs = priceSeries.map((p) => p[0]);

  //   // Determine axis time bounds: prefer xMin/xMax if provided, otherwise derive from data
  //   const minTimeMs = typeof xMin === "number" ? xMin : (timestampsMs.length ? Math.min(...timestampsMs) : null);
  //   const maxTimeMs = typeof xMax === "number" ? xMax : (timestampsMs.length ? Math.max(...timestampsMs) : null);

  //   // If caller didn't pass xMin/xMax and data is empty, compute a reasonable default window
  //   // (use last 6 months as fallback)
  //   let minTimeISO, maxTimeISO;
  //   if (minTimeMs != null && maxTimeMs != null) {
  //     minTimeISO = new Date(minTimeMs).toISOString();
  //     maxTimeISO = new Date(maxTimeMs).toISOString();
  //   } else {
  //     // fallback: end = now, start = now - 6 months (keeps chart visible)
  //     const now = Date.now();
  //     const sixMonthsMs = 1000 * 60 * 60 * 24 * 30 * 6;
  //     minTimeISO = new Date(now - sixMonthsMs).toISOString();
  //     maxTimeISO = new Date(now).toISOString();
  //   }

  //   // Determine Y-axis bounds
  //   const prices = priceSeries.map((p) => p[1]);
  //   let niceMin, niceMax;
  //   if (prices.length > 0) {
  //     const minPrice = Math.min(...prices);
  //     const maxPrice = Math.max(...prices);
  //     const diff = Math.max(1e-8, maxPrice - minPrice);
  //     const magnitude = Math.pow(10, Math.floor(Math.log10(diff)));
  //     niceMin = Math.floor(minPrice / magnitude) * magnitude;
  //     niceMax = Math.ceil(maxPrice / magnitude) * magnitude;
  //     if (niceMin === niceMax) {
  //       // tiny band if all prices equal
  //       niceMin = minPrice * 0.999;
  //       niceMax = maxPrice * 1.001;
  //     }
  //   } else {
  //     // no points in range -> keep a small band so Y-axis isn't collapsed
  //     niceMin = 0;
  //     niceMax = 1;
  //   }

  //   const xLabelFormatter = (val) => {
  //     const d = new Date(val);
  //     switch (range) {
  //       case "1D":
  //         // e.g. '12:30:00 AM'
  //         return d.toLocaleTimeString();
  //       case "1M":
  //       case "6M":
  //       case "YTD":
  //         // e.g. 'Aug 5'
  //         return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  //       case "1Y":
  //       case "3Y":
  //       case "5Y":
  //         // e.g. 'Aug 2024'
  //         return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  //       default:
  //         return d.toLocaleDateString();
  //     }
  //   };

  //   const options = {
  //     backgroundColor: 'transparent',
  //     tooltip: {
  //       trigger: "axis",
  //       backgroundColor: theme === 'dark' ? "rgba(26, 28, 42, 0.9)" : "rgba(203, 210, 246, 0.9)",
  //       borderColor: theme === 'dark' ? "rgba(58, 63, 97, 0.9)" : "rgba(118, 133, 209, 0.9)",
  //       borderWidth: 1,
  //       padding: [8, 12],
  //       textStyle: { color: theme === 'dark' ? "#fff" : "#414243ff", fontSize: 12 },
  //       axisPointer: {
  //         type: "cross",
  //         lineStyle: { color: theme === 'dark' ? "#aaa" : "#726e6eff", width: 1 },
  //         crossStyle: { color: theme === 'dark' ? "#aaa" : "#726e6eff" },
  //         label: {
  //           show: true,
  //           backgroundColor: theme === 'dark' ? "#414247ff" : "#414247ff",
  //           padding: [3, 5],
  //           borderColor: "#595757ff",
  //           borderWidth: 1,
  //           textStyle: { color: "#fff", fontSize: 12 },
  //         },
  //       },
  //       formatter: (params) => {
  //         const p = params[0];
  //         const dateVal = new Date(p.axisValue);
  //         //   const timeStr = date.toLocaleTimeString();
  //         //   const priceStr = p.data[1].toFixed(2);
  //         //   return `Time: <span style="color:#AEDFFC;margin-left:5px;">${timeStr}</span><br/>Price: <span style="color:#7CFC00;margin-left:5px;">${priceStr}</span>`;
  //         let label;
  //         switch (range) {
  //           case "1D":
  //             label = dateVal.toLocaleTimeString(); // e.g. "12:30:00 AM"
  //             break;
  //           case "1M":
  //           case "6M":
  //           case "YTD":
  //             label = dateVal.toLocaleDateString(undefined, { month: "short", day: "numeric" }); // e.g. "Aug 5"
  //             break;
  //           case "1Y":
  //           case "3Y":
  //           case "5Y":
  //             label = dateVal.toLocaleDateString(undefined, { month: "short", year: "numeric" }); // e.g. "Aug 2024"
  //             break;
  //           default:
  //             label = dateVal.toLocaleString();
  //         }
  //         const priceStr = p.data[1].toFixed(2);
  //         return `<div style="color:${theme === 'dark' ? '#dadae2ff' : '#1d1d1eff'};font-weight:600;margin-bottom:8px;">${label}</div>Price: <span style="color:${theme === 'dark' ? "#7CFC00" : "#2c5602ff"}">${priceStr}</span><br/>Units Issued: <span style="color:${theme === 'dark' ? "#2bc5fc" : "#1e80a3ff"}">${numberWithCommas(p.data[2])}</span>`;
  //       },
  //     },
  //     xAxis: {
  //       type: "time",
  //       boundaryGap: false,
  //       min: minTimeISO,
  //       max: maxTimeISO,
  //       axisLine: { lineStyle: { color: theme === 'dark' ? "#ccc" : "#75777aff" } },
  //       axisTick: { alignWithLabel: true },
  //       axisLabel: { formatter: xLabelFormatter },
  //     },
  //     yAxis: {
  //       type: "value",
  //       scale: true,
  //       position: "right",
  //       min: niceMin,
  //       max: niceMax,
  //       axisLine: { show: false },
  //       splitLine: { show: true, lineStyle: { type: "dashed", color: theme === 'dark' ? "#7a7575ff" : "#aea9a9ff" } },
  //     },
  //     grid: { top: "10px", right: "38px", bottom: "28px", left: "10px" },
  //     series: [
  //       {
  //         name: "Price",
  //         type: "line",
  //         data: priceSeries,
  //         smooth: true,
  //         showSymbol: false,
  //         lineStyle: { width: 2, color: theme === 'dark' ? "#b580b9ff" : "rgb(41, 124, 223)" },
  //         areaStyle: { opacity: 0.1, color: theme === 'dark' ? "#9a5a9eff" : "rgb(41, 124, 223)" },
  //       },
  //     ],
  //   };

  //   chartInstanceRef.current.setOption(options, { notMerge: true });
  // }, [data]);

  return <div ref={chartRef} style={{ width: "100%", height: "400px", ...style }} />;
}
